import type { ScheduledTab, UnscheduledTestTab } from "./types";
import type { SubjectAndSectionResponse } from "../app/src/components/subject-and-sections/types";
import { SUBJECT_ICONS } from "./utils";

/**
 * Icon name type - matches the IconName from @repo/ui
 * Defined here to avoid circular dependencies in shared code
 */
type IconName = string;

export interface SocialOption {
  labelKey: string;
  iconName: IconName;
  getHref: (url: string) => string;
}

export const SOCIAL_OPTIONS: SocialOption[] = [
  {
    labelKey: "share.twitter",
    iconName: "xIcon",
    getHref: (url) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
  },
  {
    labelKey: "share.facebook",
    iconName: "facebookIcon",
    getHref: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    labelKey: "share.linkedin",
    iconName: "linkedinIcon",
    getHref: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    labelKey: "share.email",
    iconName: "emailIcon",
    getHref: (url) =>
      `mailto:?subject=Check out this test&body=${encodeURIComponent(url)}`,
  },
];

/** Re-exported from utils.ts — single source of truth for subject icons */
export { SUBJECT_ICONS } from "./utils";

export const getSubjectIconKey = (subjectName: string): IconName | null => {
  const lower = subjectName.toLowerCase();
  return SUBJECT_ICONS[lower] ?? null;
};

export const SCHEDULED_TABS: { key: ScheduledTab; labelKey: string }[] = [
  { key: "upcoming", labelKey: "tab_upcoming" },
  { key: "missed", labelKey: "tab_missed" },
  { key: "attempted", labelKey: "tab_attempted" },
];

export const SCHEDULED_TAB_KEYS: readonly ScheduledTab[] = SCHEDULED_TABS.map(
  (t) => t.key,
);

export const ACTION_LABEL_KEYS: Record<string, string> = {
  in_progress: "resume",
  available: "attempt",
  coming_up: "coming_up",
  missed: "view_question_paper",
  attempted: "view_report",
};

export const SCORE_ACTION_LABEL_KEYS: Record<string, string> = {
  unregistered: "register_now",
  registered: "attempt",
  in_progress: "resume",
  missed: "view_question_paper",
  attempted: "view_report",
};

export const UNSCHEDULED_TABS: { key: UnscheduledTestTab; labelKey: string }[] =
  [
    { key: "tests", labelKey: "tab_tests" },
    { key: "attempted", labelKey: "tab_attempted" },
  ];

export const UNSCHEDULED_TAB_KEYS: readonly UnscheduledTestTab[] =
  UNSCHEDULED_TABS.map((t) => t.key);

const dummyResponse: SubjectAndSectionResponse = {
  sections: [
    {
      title: "Zoology",
      sub_sections: [
        {
          title: "Section 1",
          groups: [
            {
              id: "1",
              title: "Section 1",
              questions: [
                {
                  question_id: "ILQ-1",
                  question_type: "MCQ",
                  question_text: "Sample Question",
                  options: ["A", "B", "C", "D"],
                },
              ],
            },
            {
              id: "2",
              title: "Section 2",
              questions: [
                {
                  question_id: "ILQ-2",
                  question_type: "Very Short Answer",
                  question_text: "Another Question",
                  options: ["A", "B", "C", "D"],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      title: "Chemistry",
      sub_sections: [
        {
          title: "Section 1",
          groups: [
            {
              id: "1",
              title: "Section 1",
              questions: [
                {
                  question_id: "ILQ-1",
                  question_type: "MCQ",
                  question_text: "Sample Question",
                  options: ["A", "B", "C", "D"],
                },
              ],
            },
            {
              id: "2",
              title: "Section 2",
              questions: [
                {
                  question_id: "ILQ-2",
                  question_type: "Very Short Answer",
                  question_text: "Another Question",
                  options: ["A", "B", "C", "D"],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      title: "Biology",
      sub_sections: [],
    },
  ],
};

export const fetchSubjectAndSections =
  async (): Promise<SubjectAndSectionResponse> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(dummyResponse);
      }, 800);
    });
  };
